import { mergeStyles } from '@cian/utils/lib/shared/style';

import * as React from 'react';

import { IOtherLinksSchema } from '../../repositories/header/entities/other_links/OtherLinksSchema';
import { DealRentBanner } from '../DealRentBanner';

import { PikIcon } from '../PikIcon';

import { CloseIcon, ICONS } from './icons';
import { TDropdownType } from './interfaces';
import { TagNew } from './internal/components';
import { trackDropdownLinkClick, trackCatalogRealtorsLinkClick, trackCareerLinkClick } from './tracking';
import { IDropdownMenuLink } from '../../types/dropdownMenuLink';
import { isPikRegion } from '../../utils';

export interface IDropdownMenuProps {
  hasShadow?: boolean;
  links: IDropdownMenuLink[];
  name: TDropdownType;
  otherLinks: IOtherLinksSchema;
  catalogDevelopersUrl?: string;
  catalogRealtorsUrl?: string;
  isOpen?: boolean;
  onClose?(): void;
  onMouseLeave?(key: string): void;
  onLinkClick?(link: IDropdownMenuLink): void;
  styleHoverPanel?: { [key: string]: string };
  dealRentHeaderBannerEnabled: boolean;
  banner: React.ReactElement | null;
  regionId: number;
  pikRegions: number[];
}

export interface IDropdownMenuState {
  dropdownOver?: boolean;
  initialized?: boolean;
  mouseInTriangle?: boolean;
  parentOver?: boolean;
}

const styles = require('./dropdown_menu.css');

export class DropdownMenu extends React.Component<IDropdownMenuProps, IDropdownMenuState> {
  public constructor(props: IDropdownMenuProps) {
    super(props);

    this.state = {
      parentOver: false,
    };
  }

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  public render() {
    const { links } = this.props;

    if (!Array.isArray(links) || !links.length) {
      return <div />;
    }

    return (
      <div
        {...mergeStyles(
          styles['container'],
          this.state.initialized && styles['container--initialized'],
          this.props.hasShadow && styles['container--has_shadow'],
          styles['container--scale'],
          this.props.isOpen && styles['container--scale_over'],
          'c-header-bottom-menu-item-dropdown',
        )}
      >
        <div className="c-header-container c-header-container-border">
          <div className="c-header-dropdown-close-icon" onClick={this.props.onClose}>
            <CloseIcon />
          </div>
          <div className="c-header-container-row">{this.renderLinks()}</div>
        </div>
      </div>
    );
  }

  private renderLinks() {
    const {
      name,
      links,
      otherLinks,
      catalogDevelopersUrl,
      catalogRealtorsUrl,
      regionId,
      pikRegions,
      banner,
    } = this.props;

    const isSearchMap = name === 'rent' || name === 'sale' || name === 'newbuildings' || name === 'commercial';
    const dealRentHeaderBannerEnabled = this.props.dealRentHeaderBannerEnabled && name === 'rent';

    return (
      <div className="c-header-col c-header-left-container-skip">
        <div className={styles['list']}>
          <div className={styles['links-grid']}>
            <div className={styles['row-container']}>
              {links.map((group, groupIndex) => (
                <div key={`group-${groupIndex}`} className={styles['cell']}>
                  {group.children &&
                    group.children.map((link, linkIndex) => {
                      const cssClassLink = link.isBold || link.isTitle ? 'cell-link-title' : 'cell-link';
                      const key = `group-${groupIndex}-link-${linkIndex}`;
                      const extraLinkClass = link.className || '';

                      if (link.url) {
                        return (
                          <a
                            key={key}
                            {...mergeStyles(styles[cssClassLink], styles[extraLinkClass])}
                            href={link.url}
                            onClick={this.onLinkClick(link)}
                          >
                            {link.icon && <span className={styles['cell-link-icon']}>{ICONS[link.icon]}</span>}
                            {/* Иконка только для ПИК в МСК. В СПБ ПИК спец проект, поэтому сработает условие ниже. */}
                            {link.key === 'pik' && isPikRegion(regionId, pikRegions) && (
                              <PikIcon className={styles['cell-link-icon']} />
                            )}
                            {link.image && (
                              <span
                                className={styles['cell-link-icon']}
                                style={{
                                  backgroundImage: `url(${link.image})`,
                                }}
                              />
                            )}
                            {link.text}
                            {link.isNew && <TagNew containerClassName={styles['tag-new']} />}
                          </a>
                        );
                      }

                      if (link.isTitle) {
                        return (
                          <div key={key} className={styles[cssClassLink]}>
                            {link.icon && <span className={styles['cell-link-icon']}>{ICONS[link.icon]}</span>}
                            {link.text}
                          </div>
                        );
                      }

                      return null;
                    })}
                </div>
              ))}
              {(banner || dealRentHeaderBannerEnabled) && (
                <div className={styles['cell']}>{dealRentHeaderBannerEnabled ? <DealRentBanner /> : banner}</div>
              )}
            </div>
            <ul className={styles['horizontal-bottom-menu']}>
              <li className="another-change-location" />
              {isSearchMap && (
                <li>
                  <a href={otherLinks.map} className={styles['horizontal-link']}>
                    Поиск по карте
                  </a>
                </li>
              )}
              <li>
                <a href={otherLinks.helpCenter} className={styles['horizontal-link']}>
                  Справочный центр
                </a>
              </li>
              <li>
                <a href={catalogDevelopersUrl} className={styles['horizontal-link']}>
                  Каталог застройщиков
                </a>
              </li>
              <li>
                <a
                  href={catalogRealtorsUrl}
                  className={styles['horizontal-link']}
                  onClick={trackCatalogRealtorsLinkClick}
                >
                  Каталог риелторов
                </a>
              </li>
              <li>
                <a href={otherLinks.realtorsForum} className={styles['horizontal-link']}>
                  Вопросы риэлтору
                </a>
              </li>
              <li>
                <a
                  href={otherLinks.cianCareer}
                  className={styles['horizontal-link--accent']}
                  onClick={trackCareerLinkClick}
                >
                  Карьера в Циан
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  private onLinkClick = (link: IDropdownMenuLink) => (event: React.MouseEvent<HTMLElement>) => {
    const { analyticsEvent, analyticsLabel, url, target, analyticsCategory, analyticsAction } = link;
    const { onLinkClick: onClick } = this.props;

    event.preventDefault();
    if (analyticsLabel) {
      trackDropdownLinkClick({
        label: analyticsLabel,
        event: analyticsEvent,
        category: analyticsCategory,
        action: analyticsAction,
      });
    }

    if (onClick) {
      onClick(link);
    }

    if (url) {
      // eslint-disable-next-line no-unused-expressions
      target === '_blank' ? window.open(url) : location.assign(url);
    }
  };
}
